import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  DollarSign,
  AlertTriangle,
  TrendingUp,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  VALUATION_MULTIPLES,
  computeValuation,
  formatUsd,
} from "../../lib/valuationMultiples";

/**
 * <ValuationCalculator/> — the reusable form + result section shared by
 * the generic /valuation page and the industry-specific /valuation/:industry
 * pages.
 *
 * @param {string} initialIndustry — starting industry slug
 * @param {boolean} lockIndustry   — hides the industry selector when true
 * @param {boolean} navigateOnIndustryChange — when the dropdown IS shown,
 *   selecting a new industry navigates to that industry's landing page
 *   instead of updating local state. Used on the generic page so users can
 *   pivot to an industry-specific page for better content.
 */
export default function ValuationCalculator({
  initialIndustry = "restaurants",
  lockIndustry = false,
  navigateOnIndustryChange = false,
}) {
  const navigate = useNavigate();
  const [industry, setIndustry] = useState(initialIndustry);
  const [revenue, setRevenue] = useState("");
  const [ebitda, setEbitda] = useState("");
  const [growthPct, setGrowthPct] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const result = useMemo(() => {
    if (!submitted) return null;
    const rev = Number(revenue) || 0;
    const eb = Number(ebitda) || 0;
    const growth = growthPct === "" ? null : Number(growthPct) / 100;
    if (rev <= 0) return null;
    return computeValuation({
      industry,
      revenue: rev,
      ebitda: eb,
      growthRate: growth,
    });
  }, [submitted, industry, revenue, ebitda, growthPct]);

  const industryOptions = Object.entries(VALUATION_MULTIPLES).map(
    ([slug, data]) => ({ slug, label: data.label })
  );

  function handleIndustryChange(next) {
    if (navigateOnIndustryChange && next !== industry) {
      navigate(`/valuation/${next}`);
      return;
    }
    setIndustry(next);
  }

  function handleEstimate(e) {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      const el = document.getElementById("valuation-result");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 40);
  }

  return (
    <>
      {/* -- FORM ----------------------------------------------------------- */}
      <form
        onSubmit={handleEstimate}
        className="mt-10 bg-white border border-[#E5E0D8] rounded-2xl p-6 sm:p-8"
        data-testid="valuation-form"
      >
        <div className="grid sm:grid-cols-2 gap-5">
          {!lockIndustry && (
            <div className="sm:col-span-2">
              <label
                htmlFor="v-industry"
                className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]"
              >
                Industry
              </label>
              <select
                id="v-industry"
                value={industry}
                onChange={(e) => handleIndustryChange(e.target.value)}
                className="mt-2 w-full h-11 bg-[#F7F5F0] border border-[#E5E0D8] rounded-md px-3 text-[15px] text-[#1F382E] focus:outline-none focus:border-[#1F382E]/60"
                data-testid="valuation-industry-select"
              >
                {industryOptions.map((o) => (
                  <option key={o.slug} value={o.slug}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label
              htmlFor="v-revenue"
              className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]"
            >
              Annual revenue (USD)
            </label>
            <div className="mt-2 relative">
              <DollarSign
                size={14}
                strokeWidth={1.5}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C665D]"
              />
              <Input
                id="v-revenue"
                type="number"
                inputMode="decimal"
                step="1000"
                min="0"
                placeholder="e.g. 1,400,000"
                value={revenue}
                onChange={(e) => setRevenue(e.target.value)}
                className="h-11 bg-[#F7F5F0] border-[#E5E0D8] pl-8 text-[15px]"
                data-testid="valuation-revenue-input"
                required
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="v-ebitda"
              className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]"
            >
              Annual EBITDA (USD)
            </label>
            <div className="mt-2 relative">
              <DollarSign
                size={14}
                strokeWidth={1.5}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C665D]"
              />
              <Input
                id="v-ebitda"
                type="number"
                inputMode="decimal"
                step="1000"
                placeholder="e.g. 180,000"
                value={ebitda}
                onChange={(e) => setEbitda(e.target.value)}
                className="h-11 bg-[#F7F5F0] border-[#E5E0D8] pl-8 text-[15px]"
                data-testid="valuation-ebitda-input"
                required
              />
            </div>
            <p className="text-[11.5px] text-[#5C665D] mt-1.5 leading-snug">
              Earnings before interest, tax, depreciation, amortisation.
              Owner-comp addback ≈ SDE.
            </p>
          </div>

          <div className="sm:col-span-2">
            <label
              htmlFor="v-growth"
              className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]"
            >
              Expected annual growth (%) —{" "}
              <span className="normal-case tracking-normal text-[#5C665D]/70">
                optional, default{" "}
                {(
                  (VALUATION_MULTIPLES[industry]?.typicalGrowth || 0.05) * 100
                ).toFixed(0)}
                % for {VALUATION_MULTIPLES[industry]?.label.toLowerCase()}
              </span>
            </label>
            <div className="mt-2 relative max-w-[240px]">
              <Input
                id="v-growth"
                type="number"
                inputMode="decimal"
                step="0.5"
                placeholder={`${(
                  (VALUATION_MULTIPLES[industry]?.typicalGrowth || 0.05) * 100
                ).toFixed(0)}`}
                value={growthPct}
                onChange={(e) => setGrowthPct(e.target.value)}
                className="h-11 bg-[#F7F5F0] border-[#E5E0D8] pr-8 text-[15px]"
                data-testid="valuation-growth-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C665D] text-sm">
                %
              </span>
            </div>
          </div>
        </div>

        <Button
          type="submit"
          className="mt-7 w-full sm:w-auto bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-11 px-8 transition-all duration-300"
          data-testid="valuation-estimate-btn"
        >
          Estimate valuation{" "}
          <ArrowRight size={14} strokeWidth={1.5} className="ml-2" />
        </Button>
      </form>

      {/* -- RESULT --------------------------------------------------------- */}
      {result && result.range && (
        <section
          id="valuation-result"
          className="mt-10 scroll-mt-16"
          data-testid="valuation-result"
        >
          <div className="bg-[#1F382E] text-[#F7F5F0] rounded-2xl p-6 sm:p-10">
            <p className="text-[11px] uppercase tracking-[0.24em] text-[#F7F5F0]/70">
              Estimated valuation
            </p>
            <p
              className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-2 leading-none"
              data-testid="valuation-range"
            >
              {formatUsd(result.range.low)}
              <span className="text-[#C26D5C]"> — </span>
              {formatUsd(result.range.high)}
            </p>
            <p className="text-[#F7F5F0]/70 text-sm mt-4 leading-relaxed">
              Range of {result.methods.filter((m) => m.applicable).length}{" "}
              valuation methods · midpoint ≈ {formatUsd(result.range.mid)}.
              Applies a{" "}
              <span className="text-[#F7F5F0]">
                {(result.sizeDiscount * 100).toFixed(0)}% size discount
              </span>{" "}
              for the {result.sizeBucket} bucket.
            </p>
          </div>

          {result.warnings.length > 0 && (
            <div
              className="mt-4 flex items-start gap-3 bg-[#C26D5C]/10 border border-[#C26D5C]/30 rounded-xl p-4"
              data-testid="valuation-warning"
            >
              <AlertTriangle
                size={16}
                strokeWidth={1.5}
                className="text-[#C26D5C] mt-0.5 shrink-0"
              />
              <div className="text-[13.5px] text-[#1A1A1A] leading-relaxed">
                {result.warnings[0]}
              </div>
            </div>
          )}

          <div
            className="mt-6 bg-white border border-[#E5E0D8] rounded-2xl overflow-hidden"
            data-testid="valuation-methods"
          >
            <div className="px-5 sm:px-6 py-4 border-b border-[#E5E0D8]">
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]">
                How we got there
              </p>
              <p className="text-sm text-[#1A1A1A] mt-1">
                Four independent methods, each with its own multiple and
                source. The final range spans the applicable estimates.
              </p>
            </div>
            <div className="divide-y divide-[#E5E0D8]">
              {result.methods.map((m) => {
                const dim = !m.applicable || m.informationalOnly;
                return (
                  <div
                    key={m.id}
                    className={`px-5 sm:px-6 py-5 ${dim ? "bg-[#F7F5F0]" : ""}`}
                    data-testid={`valuation-method-${m.id}`}
                  >
                    <div className="flex items-baseline justify-between gap-4 flex-wrap">
                      <p className="font-medium text-[#1F382E] text-[15.5px]">
                        {m.label}
                        {!m.applicable && !m.informationalOnly && (
                          <span className="ml-2 text-[10.5px] uppercase tracking-[0.18em] text-[#C26D5C]">
                            n/a
                          </span>
                        )}
                        {m.informationalOnly &&
                          m.applicable !== undefined && (
                            <span className="ml-2 text-[10.5px] uppercase tracking-[0.18em] text-[#5C665D]">
                              informational
                            </span>
                          )}
                      </p>
                      <p
                        className={`font-serif-display text-2xl tabular-nums ${m.informationalOnly ? "text-[#5C665D]" : "text-[#1F382E]"}`}
                        data-testid={`valuation-method-${m.id}-value`}
                      >
                        {m.applicable || m.informationalOnly
                          ? formatUsd(m.value)
                          : "—"}
                      </p>
                    </div>
                    <p className="text-[13px] text-[#1A1A1A] mt-1.5 font-mono">
                      {m.formula}
                    </p>
                    <p className="text-[12px] text-[#5C665D] mt-2 leading-snug">
                      Source: {m.source}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {result.industryData?.notes && (
            <div className="mt-6 bg-white border-l-2 border-[#C26D5C] border-y border-r border-y-[#E5E0D8] border-r-[#E5E0D8] rounded-r-xl rounded-l-sm p-5">
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#C26D5C]">
                For {result.industryData.label.toLowerCase()}
              </p>
              <p className="text-[14.5px] text-[#1A1A1A] leading-relaxed mt-2">
                {result.industryData.notes}
              </p>
            </div>
          )}

          <div className="mt-8 bg-[#1F382E]/5 border border-[#1F382E]/10 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <div className="flex items-start gap-3 max-w-md">
              <TrendingUp
                size={18}
                strokeWidth={1.5}
                className="text-[#C26D5C] mt-0.5 shrink-0"
              />
              <div>
                <p className="font-serif-display text-xl text-[#1F382E] tracking-tight leading-tight">
                  A healthier business commands a higher multiple.
                </p>
                <p className="text-sm text-[#5C665D] mt-2 leading-relaxed">
                  A CFO Grade of A+ can push buyers 15-25% above this range. A
                  grade of D can drag it 25-40% below. Run yours in 5 minutes
                  to see where you land.
                </p>
              </div>
            </div>
            <Link
              to={`/analyze?utm_source=valuation&utm_campaign=${industry}-results`}
            >
              <Button
                className="bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-11 px-6 transition-all duration-300 whitespace-nowrap"
                data-testid="valuation-cta-grade"
              >
                Get my CFO Grade{" "}
                <ArrowRight size={14} strokeWidth={1.5} className="ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      )}
    </>
  );
}
